/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import cn from 'classnames';

import { checkPasswordConditions } from 'components/Auth/Signup/helpers';
import Render from 'components/render';
import { noop } from 'helpers';
import { isEmpty } from 'helpers/ramda';
import {
  EyeClosed,
  EyeOpen,
  ValidationCorrect,
  CircularExclamationWarning,
  AngleUp,
  AngleDown,
} from 'styles/assets/icons';
import { Box } from 'UIKit';
import IconButton from 'UIKit/IconButton';

import PasswordCriteriaPopUp from './PasswordCriteriaPopUp';
import styles from './styles.module.scss';

interface ErrorInfo {
  error?: boolean;
  errorMessage?: string | React.ReactNode;
  errorCode?: string;
}

export interface AuthInputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  value: string;
  label?: string;
  showEye?: boolean;
  hideValidationCorrectIcon?: boolean;
  passwordScore?: number;
  errorInfo?: ErrorInfo;
  testID: string;
  showValidation?: boolean;
  validationIsLoading?: boolean;
  formFieldClassname?: string;
  pwdCriteriaPopupClassname?: string;
  clickLabelToExpand?: boolean;
}

const AuthInputField = React.forwardRef(
  (props: AuthInputFieldProps, forwardedRef: any) => {
    const defaultRef = useRef(null);
    const ref = forwardedRef ?? defaultRef;
    const {
      label = '',
      showEye = false,
      hideValidationCorrectIcon = false,
      passwordScore,
      errorInfo = {
        error: false,
        errorCode: '',
        errorMessage: '',
      },
      testID = 'inputField',
      showValidation = true,
      validationIsLoading,

      value,
      id,
      type,
      formFieldClassname = '',
      pwdCriteriaPopupClassname = '',
      clickLabelToExpand,
      ...restInputProps
    } = props;

    const [showText, setShowText] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [showRefCodeField, setShowRefCodeField] = useState(false);

    const toggleEye = () => {
      if (type !== 'password') return;
      const inputElement = ref?.current;
      if (!inputElement) return;
      const cursorPosition = inputElement.selectionStart;
      setShowText(!showText);
      setTimeout(() => {
        inputElement.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    };

    const onFocus: React.FocusEventHandler<HTMLInputElement> = e => {
      if (props.onFocus) props.onFocus(e);
      setIsFocused(true);
    };

    const onBlur: React.FocusEventHandler<HTMLInputElement> = e => {
      if (props.onBlur) props.onBlur(e);
      setIsFocused(false);
    };

    const toggleShowRefField = () => {
      setShowRefCodeField(!showRefCodeField);
    };

    const handleClickFormField = () => {
      if (ref && ref.current) {
        ref?.current?.focus?.();
      }
    };

    // Component styling and render logic.
    const formFieldStyles = cn(styles.formField, {
      [styles.noLabel]: !label,
      [styles.fieldError]:
        type === 'password' ? errorInfo.error : errorInfo.error && !isFocused,
      [styles.fieldFocused]: isFocused,
      [formFieldClassname]: formFieldClassname,
    });

    const showPasswordCriteriaPopUp: boolean =
      type === 'password' &&
      Boolean(value.length) &&
      isFocused &&
      !hideValidationCorrectIcon;

    const showValidationIcon = value.length !== 0 && showValidation;

    return (
      (<div className={styles.container} data-palette="AuthInputField">
        <Box renderWhen={!isEmpty(label)} className={styles.label}>
          {label}
          <Render when={clickLabelToExpand}>
            <IconButton
              data-testid="referralCodeToggleBtn"
              icon={
                <Render
                  when={showRefCodeField}
                  else={<AngleDown width={10} height={6} />}
                >
                  <AngleUp width={10} height={6} />
                </Render>
              }
              onClick={toggleShowRefField}
              className={styles.refCodeToggleBtn}
              onKeyDown={noop}
            />
          </Render>
        </Box>
        <Box
          renderWhen={clickLabelToExpand ? showRefCodeField : true}
          className={formFieldStyles}
          onClick={handleClickFormField}
          data-testid="formFieldDiv"
        >
          <input
            {...restInputProps}
            value={value}
            id={id}
            data-testid={testID}
            type={showText ? 'text' : type}
            className={cn(styles.input, {
              [styles.noLabel]: label === '',
            })}
            onFocus={onFocus}
            onBlur={onBlur}
            spellCheck="false"
            ref={ref}
          />

          <div className={styles.iconSection}>
            <Render when={showEye}>
              <button
                type="button"
                onClick={toggleEye}
                className={styles.eyeBox}
                data-testid={`${testID}-showHideTextButton`}
              >
                <Render when={showText} else={<EyeClosed />}>
                  <EyeOpen />
                </Render>
              </button>
            </Render>

            <Render when={showValidationIcon}>
              <Render
                when={validationIsLoading}
                else={
                  <Render
                    when={errorInfo.error}
                    else={
                      <Render when={!hideValidationCorrectIcon}>
                        <ValidationCorrect />
                      </Render>
                    }
                  >
                    <CircularExclamationWarning />
                  </Render>
                }
              >
                <CircularProgress color="inherit" size={16} />
              </Render>
            </Render>
          </div>
        </Box>
        <Render when={errorInfo.errorMessage}>
          <Render
            when={typeof errorInfo.errorMessage === 'string'}
            else={errorInfo.errorMessage}
          >
            <div className={styles.errorMessage}>{errorInfo.errorMessage}</div>
          </Render>
        </Render>
        <Render when={showPasswordCriteriaPopUp}>
          <PasswordCriteriaPopUp
            conditionsStatus={checkPasswordConditions(value)}
            score={passwordScore}
            value={value}
            className={pwdCriteriaPopupClassname}
          />
        </Render>
      </div>)
    );
  }
);

AuthInputField.defaultProps = {
  label: '',
  showEye: false,
  hideValidationCorrectIcon: false,
  passwordScore: 0,
  errorInfo: { error: false, errorMessage: '', errorCode: '' },
  showValidation: true,
  validationIsLoading: false,
  formFieldClassname: '',
  pwdCriteriaPopupClassname: '',
  clickLabelToExpand: false,
};

export default AuthInputField;
